import React, { useState, useEffect, useCallback } from 'react';
import api from '../../lib/api';
import { toast } from 'sonner';
import {
  GripVertical, Ticket, Shield, AlertTriangle, TrendingUp,
  BookOpen, Zap, Eye, EyeOff, Save, RotateCcw
} from 'lucide-react';
import { Button } from '../ui/button';

export default function WidgetDashboard() {
  const [widgets, setWidgets] = useState([]);
  const [widgetData, setWidgetData] = useState({});
  const [loading, setLoading] = useState(true);
  const [draggedIdx, setDraggedIdx] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);

  const fetchLayout = useCallback(async () => {
    try {
      const { data } = await api.get('/api/widgets/layout');
      setWidgets(data.widgets || []);
    } catch (e) { /* handled */ }
  }, []);

  const fetchWidgetData = useCallback(async () => {
    try {
      const results = await Promise.allSettled([
        api.get('/api/tickets?limit=5'),
        api.get('/api/sla/analytics?days=30'),
        api.get('/api/cyber/alerts?limit=5'),
        api.get('/api/growth/audits'),
        api.get('/api/kb/articles?limit=5'),
      ]);
      const d = (i) => results[i].status === 'fulfilled' ? results[i].value.data : null;
      setWidgetData({
        tickets: d(0),
        sla: d(1),
        alerts: d(2),
        audits: d(3),
        kb: d(4),
      });
    } catch (e) { /* handled */ }
  }, []);

  useEffect(() => {
    (async () => {
      await Promise.all([fetchLayout(), fetchWidgetData()]);
      setLoading(false);
    })();
  }, [fetchLayout, fetchWidgetData]);

  const handleSaveLayout = async () => {
    try {
      await api.put('/api/widgets/layout', { widgets });
      setHasChanges(false);
      toast.success('Dashboard layout saved');
    } catch { toast.error('Save failed'); }
  };

  const handleReset = async () => {
    try {
      const defaults = [
        { id: 'ticket_stats', title: 'Ticket Overview', type: 'ticket_stats', order: 0, visible: true },
        { id: 'sla_gauge', title: 'SLA Compliance', type: 'sla_gauge', order: 1, visible: true },
        { id: 'recent_alerts', title: 'Recent Alerts', type: 'recent_alerts', order: 2, visible: true },
        { id: 'audit_score', title: 'Latest Audit', type: 'audit_score', order: 3, visible: true },
        { id: 'kb_popular', title: 'Popular Articles', type: 'kb_popular', order: 4, visible: true },
        { id: 'quick_actions', title: 'Quick Actions', type: 'quick_actions', order: 5, visible: true },
      ];
      setWidgets(defaults);
      await api.put('/api/widgets/layout', { widgets: defaults });
      setHasChanges(false);
      toast.success('Layout reset to default');
    } catch { toast.error('Reset failed'); }
  };

  const toggleWidget = (idx) => {
    setWidgets(prev => prev.map((w, i) => i === idx ? { ...w, visible: !w.visible } : w));
    setHasChanges(true);
  };

  const handleDragStart = (idx) => setDraggedIdx(idx);
  const handleDragOver = (e) => e.preventDefault();
  const handleDrop = (dropIdx) => {
    if (draggedIdx === null || draggedIdx === dropIdx) return;
    setWidgets(prev => {
      const copy = [...prev];
      const [moved] = copy.splice(draggedIdx, 1);
      copy.splice(dropIdx, 0, moved);
      return copy.map((w, i) => ({ ...w, order: i }));
    });
    setDraggedIdx(null);
    setHasChanges(true);
  };

  if (loading) return null;

  const visibleWidgets = widgets.filter(w => w.visible);
  const tickets = widgetData.tickets;
  const sla = widgetData.sla;
  const alerts = widgetData.alerts;
  const audits = widgetData.audits;
  const kb = widgetData.kb;

  const openCount = (tickets?.tickets || []).filter(t => ['new', 'assigned', 'in_progress'].includes(t.status)).length;
  const resolvedCount = (tickets?.tickets || []).filter(t => ['resolved', 'closed'].includes(t.status)).length;
  const latestAudit = (audits?.audits || [])[0];

  const renderWidget = (widget) => {
    switch (widget.type) {
      case 'ticket_stats':
        return (
          <div className="grid grid-cols-3 gap-3">
            <div className="bg-gray-50 rounded-lg p-3 text-center">
              <p className="text-2xl font-bold text-gray-900">{tickets?.total || 0}</p>
              <p className="text-[10px] text-gray-500">Total</p>
            </div>
            <div className="bg-amber-50 rounded-lg p-3 text-center">
              <p className="text-2xl font-bold text-amber-600">{openCount}</p>
              <p className="text-[10px] text-amber-600">Open</p>
            </div>
            <div className="bg-emerald-50 rounded-lg p-3 text-center">
              <p className="text-2xl font-bold text-emerald-600">{resolvedCount}</p>
              <p className="text-[10px] text-emerald-600">Resolved</p>
            </div>
          </div>
        );

      case 'sla_gauge': {
        const pct = sla?.compliance_pct || 0;
        const color = pct >= 90 ? 'text-emerald-600' : pct >= 70 ? 'text-amber-600' : 'text-red-600';
        const bg = pct >= 90 ? 'bg-emerald-500' : pct >= 70 ? 'bg-amber-500' : 'bg-red-500';
        return (
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className={`text-3xl font-bold ${color}`}>{pct}%</span>
              <span className="text-xs text-gray-400">{sla?.period_days || 30}d</span>
            </div>
            <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
              <div className={`h-full rounded-full ${bg} transition-all`} style={{ width: `${pct}%` }} />
            </div>
            <div className="flex justify-between mt-2 text-[10px] text-gray-400">
              <span>{sla?.total_tickets || 0} tickets</span>
              <span>{sla?.breach_count || 0} breached</span>
            </div>
          </div>
        );
      }

      case 'recent_alerts':
        return (
          <div className="space-y-2">
            {(alerts?.alerts || []).slice(0, 4).map(a => (
              <div key={a.id} className="flex items-center justify-between p-2 rounded bg-gray-50 text-xs">
                <div className="flex items-center gap-2 min-w-0">
                  <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${a.severity === 'critical' ? 'bg-red-500' : a.severity === 'high' ? 'bg-orange-500' : 'bg-amber-400'}`} />
                  <span className="truncate text-gray-700">{a.rule_name}</span>
                </div>
                <span className="text-[10px] text-gray-400 shrink-0">{a.hostname}</span>
              </div>
            ))}
            {(alerts?.alerts || []).length === 0 && <p className="text-xs text-gray-400 text-center py-2">No alerts</p>}
          </div>
        );

      case 'audit_score':
        if (!latestAudit) return <p className="text-xs text-gray-400 text-center py-4">No audits yet</p>;
        const score = latestAudit.analysis?.overall_score || 0;
        const scoreColor = score >= 75 ? 'text-emerald-600' : score >= 50 ? 'text-amber-600' : 'text-red-600';
        return (
          <div className="text-center">
            <p className={`text-4xl font-bold ${scoreColor}`}>{score}</p>
            <p className="text-xs text-gray-500 mt-1">{latestAudit.business_info?.name || 'Latest Audit'}</p>
            <p className="text-[10px] text-gray-400">{new Date(latestAudit.created_at).toLocaleDateString()}</p>
          </div>
        );

      case 'kb_popular':
        return (
          <div className="space-y-2">
            {(kb?.articles || []).slice(0, 4).map(a => (
              <div key={a.id} className="flex items-center justify-between p-2 rounded bg-gray-50 text-xs">
                <span className="truncate text-gray-700">{a.title}</span>
                <div className="flex items-center gap-2 shrink-0 text-[10px] text-gray-400">
                  <span>{a.views} views</span>
                </div>
              </div>
            ))}
            {(kb?.articles || []).length === 0 && <p className="text-xs text-gray-400 text-center py-2">No articles</p>}
          </div>
        );

      case 'quick_actions':
        return (
          <div className="grid grid-cols-2 gap-2">
            <a href="/tickets/new" className="flex items-center gap-2 p-3 rounded-lg bg-[#D4A843]/5 text-[#D4A843] text-xs font-medium hover:bg-[#D4A843]/10 transition-colors">
              <Ticket className="w-4 h-4" /> New Ticket
            </a>
            <a href="/cyber" className="flex items-center gap-2 p-3 rounded-lg bg-red-50 text-red-600 text-xs font-medium hover:bg-red-100 transition-colors">
              <Shield className="w-4 h-4" /> Cyber Ops
            </a>
            <a href="/growth" className="flex items-center gap-2 p-3 rounded-lg bg-emerald-50 text-emerald-600 text-xs font-medium hover:bg-emerald-100 transition-colors">
              <TrendingUp className="w-4 h-4" /> Growth Hub
            </a>
            <a href="/kb" className="flex items-center gap-2 p-3 rounded-lg bg-amber-50 text-amber-600 text-xs font-medium hover:bg-amber-100 transition-colors">
              <BookOpen className="w-4 h-4" /> Knowledge Base
            </a>
          </div>
        );

      default:
        return <p className="text-xs text-gray-400">Unknown widget</p>;
    }
  };

  const WIDGET_ICONS = {
    ticket_stats: Ticket,
    sla_gauge: Shield,
    recent_alerts: AlertTriangle,
    audit_score: TrendingUp,
    kb_popular: BookOpen,
    quick_actions: Zap,
  };

  return (
    <div className="space-y-4" data-testid="widget-dashboard">
      <div className="flex items-center justify-between">
        <p className="text-xs text-gray-400">Drag widgets to reorder. Toggle visibility with the eye icon.</p>
        <div className="flex gap-2">
          {hasChanges && (
            <Button size="sm" onClick={handleSaveLayout} className="bg-[#D4A843] hover:bg-[#E8C972] text-[#0A0A0A] font-semibold rounded-lg text-xs h-7" data-testid="save-layout-btn">
              <Save className="w-3 h-3 mr-1" /> Save Layout
            </Button>
          )}
          <Button size="sm" variant="outline" onClick={handleReset} className="rounded-lg text-xs h-7" data-testid="reset-layout-btn">
            <RotateCcw className="w-3 h-3 mr-1" /> Reset
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {widgets.map((widget, idx) => {
          const Icon = WIDGET_ICONS[widget.type] || Zap;
          return (
            <div
              key={widget.id}
              draggable
              onDragStart={() => handleDragStart(idx)}
              onDragOver={handleDragOver}
              onDrop={() => handleDrop(idx)}
              className={`bg-white border rounded-lg transition-all ${
                widget.visible ? 'border-gray-200' : 'border-dashed border-gray-200 opacity-40'
              } ${draggedIdx === idx ? 'shadow-lg scale-[1.02]' : ''}`}
              data-testid={`widget-${widget.id}`}
            >
              <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-100">
                <div className="flex items-center gap-2">
                  <GripVertical className="w-3.5 h-3.5 text-gray-300 cursor-grab" />
                  <Icon className="w-3.5 h-3.5 text-[#D4A843]" />
                  <span className="text-xs font-semibold text-gray-700">{widget.title}</span>
                </div>
                <button onClick={() => toggleWidget(idx)} className="text-gray-400 hover:text-gray-600" data-testid={`toggle-${widget.id}`}>
                  {widget.visible ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
                </button>
              </div>
              {widget.visible && (
                <div className="p-4">
                  {renderWidget(widget)}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
